import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { AuthenticationInitialState } from '@Features/Authentication/AuthenticationSlice/Authentication.slice.types'
import {
	LoginRequestPayload,
	CompleteProfileRequestPayload,
	VerifyPhoneRequestPayload,
	VerifyPhoneResponsePayload, AuthUserData
} from '@Features/Authentication/AuthenticationApi/Authentication.api.types'
import AuthenticationApi from '@Features/Authentication/AuthenticationApi/Authentication.api'
import LocalStorageKeys from '@Constants/LocalStorageKeys'
import { SignupRequestPayload } from '../AuthenticationApi/Authentication.api.types'
import { BaseApi } from '../../Base/BaseApi/BaseApi'
import { resetUnauthorizedGuard } from '../../Base/BaseApi/handleUnauthorized'
import { setCsrfToken, clearCsrfToken } from '@Api/csrfToken'

// export const setAuthData = createAction<AuthUserData, string>('auth/auth-obj')
// Partial: most callers pass only `redirect` to steer the post-enrollment flow.
// Typing it as the full AuthUserData forced an `as any` at every one of those
// sites — casts at exactly the places a shape change needed to be caught.
export const setAuthData = createAsyncThunk('auth/auth-obj', (authObj: Partial<AuthUserData>, { rejectWithValue }) => {
	try {
		return Promise.resolve(authObj)
	} catch (error) {
		return rejectWithValue(error)
	}
})

/**
 * Resolve session state from the server on boot.
 *
 * Returns null for an anonymous visitor rather than rejecting, so a 401 here is
 * an expected answer ("not signed in") and never triggers the terminal
 * unauthorized redirect — which would put a logged-out visitor into a login
 * loop on the public landing page.
 */
export const bootstrapAuthAction = createAsyncThunk('auth/bootstrap', async (options?: { quiet?: boolean }) => {
	const ask = async () => {
		const { data } = await AuthenticationApi.me()
		const deviceUnverified = Boolean(data?.data?.deviceUnverified)
		// The only way the portal can obtain this — it is not readable from a
		// cookie, because the API is on a different host.
		setCsrfToken(data?.data?.csrfToken)
		return {
			user: data?.data?.user ?? null,
			deviceUnverified,
			// Restores the onboarding step across a reload. `checkAuthAction` used
			// to rebuild this from the localStorage blob on every boot; with the
			// blob gone a student who signs up and refreshes would browse freely
			// with an unverified phone, because RoutingProvider and the Header both
			// gate on `redirect`. /auth/me reports it via the same checkRedirection
			// the dedicated endpoint uses, so the app-settings switches guarding
			// each step (registration_fees, phone_verification_required) apply here
			// too — and 104 stays subject to whatever each portal's
			// `excludedRedirections` policy does with it.
			redirect: data?.data?.redirect ?? null,
			// Widget identity rides the same boot answer; null means don't inject.
			chat: data?.data?.chat ?? null,
		}
	}

	try {
		return await ask()
	} catch (error) {
		// Only a 401 means "not signed in". Any other failure — a 500, a CORS
		// hiccup, an offline blip — used to resolve as anonymous and hard-bounce
		// a student with a perfectly valid cookie to /login. Retry once, then
		// give up rather than treating a network fault as a logout.
		const status = (error as any)?.status ?? (error as any)?.response?.status
		if (status !== 401) {
			try {
				return await ask()
			} catch {
				// fall through
			}
			// Quiet revalidation (bfcache restore / tab refocus): an unreachable
			// server is not "signed out" — keep the state we have rather than
			// downgrading a live session over a network blip. A 401 still falls
			// through below: that IS the server answering "not signed in".
			if (options?.quiet) return null
		}
		return { user: null, deviceUnverified: false, redirect: null, chat: null }
	}
})

/**
 * End the session server-side, then clear local state.
 *
 * The server call comes first and its failure is swallowed: if it fails the
 * user still expects to be signed out locally, and the session will expire on
 * its own. Leaving them apparently signed in would be worse.
 *
 * Callers MUST await this before any `window.location.assign` — the navigation
 * cancels an in-flight request, so an un-awaited logout leaves the session
 * alive on the server while the UI claims otherwise.
 */
export const logoutRequestAction = createAsyncThunk('auth/logout-request', async (_, { dispatch }) => {
	try {
		await AuthenticationApi.logout()
	} catch (error) {
		// best-effort
	}
	dispatch(authenticationSlice.actions.logoutAction())
})

/**
 * Admin impersonation handoff: exchange a one-time code for a session.
 *
 * Failure is silent by design — the code is single-use, so a refresh of the
 * embed replays a spent code and an error toast there would be noise.
 */
export const redeemImpersonationAction = createAsyncThunk('auth/impersonate-redeem', async (code: string) => {
	const { data } = await AuthenticationApi.redeemImpersonation(code)
	setCsrfToken(data?.data?.csrfToken)
	return data?.data?.user ?? null
})

/**
 * Codes the server returns in `data.redirect` to say what the student must do
 * before the app is usable. Mirrors Codes.Error.Redirect in the backend.
 */
export const RedirectCodes = {
	PHONE_VERIFICATION: 101,
	COMPLETE_PROFILE: 103,
	REGISTRATION_PAYMENT: 104,
	/** Signed in, but on a browser this account has not been seen on before. */
	DEVICE_VERIFICATION: 105,
} as const

// async thunks
export const loginAction = createAsyncThunk('auth/login', async (authObject: LoginRequestPayload, { rejectWithValue }) => {
	try {
		const { data } = await AuthenticationApi.login(authObject)
		setCsrfToken(data?.data?.csrfToken)
		return data
	} catch (error) {
		return rejectWithValue(error)
	}
})
export const signupAction = createAsyncThunk('auth/signup', async (form: SignupRequestPayload, { rejectWithValue }) => {
	try {
		const { data } = await AuthenticationApi.signup(form)
		setCsrfToken(data?.data?.csrfToken)
		return data
	} catch (error) {
		return rejectWithValue(error)
	}
})

export const verifyCodeAction = createAsyncThunk('auth/verify-phone', async (form: VerifyPhoneRequestPayload) => {
	const { data } = await AuthenticationApi.verifyPhone(form)
	return data
})

export const completeProfileAction = createAsyncThunk(
	'auth/complete-profile',
	async (completeProfilePayload: CompleteProfileRequestPayload) => {
		const { data } = await AuthenticationApi.completeProfile(completeProfilePayload)
		return data
	}
)

const initialState: AuthenticationInitialState = {
	isAuth: false,
	authReady: false,
	deviceUnverified: false,
	isProfileCompleted: false,
	user: null,
	redirect: null,
	chat: null,
	isAuthSignup: false,
	needRedirect: false,
	settingAuthDataCompleted: null,
	loaders: {
		login: 'idle',
		profile: 'idle',
		signup: 'idle',
	},
}

const authenticationSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		/**
		 * Session state is the server's answer now, not a guess from localStorage.
		 *
		 * This used to read a JSON blob out of localStorage and set isAuth=true if
		 * it merely EXISTED — never validated. So the app rendered as signed in
		 * against a token the server had already rejected, and only discovered
		 * otherwise when some request happened to 401.
		 *
		 * The credential is an httpOnly cookie now and cannot be read here at all,
		 * which is the point. `bootstrapAuthAction` asks /auth/me instead.
		 */
		checkAuthAction: (state: any) => {
			// Retained as a no-op entry point so existing callers keep working;
			// the real resolution happens in bootstrapAuthAction.
			state.isAuth = state.isAuth === true
		},
		/** The device passed its challenge; everything it was gated from is open now. */
		deviceVerifiedAction: (state: any) => {
			state.deviceUnverified = false
		},
		/**
		 * Clears LOCAL state only.
		 *
		 * Ending the session server-side is `logoutRequestAction` — this reducer is
		 * also what runs after a terminal 401, where there is no session left to
		 * end. Previously this WAS the entire logout: localStorage was cleared
		 * while the token stayed valid on the server until it expired, which (with
		 * expiresIn commented out) was never.
		 *
		 * `clearCsrfToken()` belongs here in the reducer rather than in the thunk:
		 * redux-state-sync broadcasts this action to other tabs, and each tab's
		 * reducer run is what clears that tab's own in-memory token. In the thunk
		 * it would only ever clear the tab that initiated the logout.
		 */
		logoutAction: (state: any) => {
			clearCsrfToken()
			state.deviceUnverified = false
			// Legacy blob. Nothing writes it any more; removing it here clears out
			// whatever a pre-cutover session left behind.
			localStorage.removeItem(LocalStorageKeys.AUTH)
			state.isAuth = false
			state.user = null
			// The widget teardown keys off this going null. Two of the three
			// logout paths hard-reload (which also removes the widget), but the
			// command-palette logout is still client-side and needs it.
			state.chat = null
			state.loaders.login = 'idle'
			state.needRedirect = false
			state.redirect = undefined
		},
	},
	extraReducers: builder => {
		// login
		builder
			.addCase(loginAction.pending, state => {
				state.loaders.login = 'pending'
			})
			.addCase(loginAction.fulfilled, (state, { payload }) => {
				// Loader first and unconditionally — same reasoning as signup below: a
				// 200 with an unexpected body must not throw mid-reducer and strand the
				// button at 'pending'.
				state.loaders.login = 'fulfilled'
				const data = payload?.data
				if (!data?.user) return
				// A fresh sign-in retires the previous expiry, so a later one on the
				// same path is not swallowed by the once-per-path guard.
				resetUnauthorizedGuard()
				state.deviceUnverified = data.redirect === RedirectCodes.DEVICE_VERIFICATION
				state.user = data.user
				state.redirect = data.redirect
				// Same contract as /auth/me: the widget can mount right after login.
				state.chat = data.chat ?? null
				// No localStorage write: the session lives in an httpOnly cookie the
				// server has already set on this response. Mirroring it here is what
				// made a stale blob look like a live session.
				state.isAuth = true
				state.authReady = true
			})
			.addCase(loginAction.rejected, state => {
				state.loaders.login = 'rejected'
			})
		//signup
		builder
			.addCase(signupAction.pending, state => {
				state.loaders.signup = 'pending'
			})
			.addCase(signupAction.fulfilled, (state, { payload }) => {
				// The loader resolves unconditionally and first: this used to set
				// `loaders.login` (a typo) and destructure `payload.data` blindly, so a
				// 200 with an unexpected body threw mid-reducer, the state update was
				// discarded, and `loaders.signup` stayed 'pending' — an infinite
				// spinner on the submit button with no feedback.
				state.loaders.signup = 'fulfilled'
				const data = payload?.data
				if (!data?.user) return
				state.user = data.user
				state.isAuth = true
				state.authReady = true
				state.isAuthSignup = true
				state.redirect = data.redirect
			})
			.addCase(signupAction.rejected, state => {
				state.loaders.signup = 'rejected'
			})

		builder.addCase(setAuthData.pending, state => {
			state.settingAuthDataCompleted = null
		})
		builder.addCase(setAuthData.fulfilled, (state, action) => {
			state.settingAuthDataCompleted = false
			const { user, redirect } = action.payload
			state.redirect = redirect
			state.needRedirect = redirect ? true : false
			// Several callers dispatch this with only a `redirect` to steer the
			// post-enrollment flow. Those must not be read as a sign-in, and must
			// not pre-empt bootstrapAuthAction's answer.
			if (user) {
				state.user = user
				state.isAuth = true
				state.authReady = true
			}
			state.settingAuthDataCompleted = true
		})

		// Boot: /auth/me is the only thing that can answer "is this session live".
		// authReady exists so the UI can tell "not signed in" apart from "haven't
		// asked yet" — without it every guarded view flashes its logged-out state
		// on load, and anything that redirects on !isAuth bounces a valid session
		// to /login before the answer arrives.
		builder
			.addCase(bootstrapAuthAction.pending, (state, action) => {
				// Quiet revalidations keep authReady true: flipping it unmounts every
				// authReady-gated subtree mid-session (the same hazard that keeps
				// auth/bootstrap out of cross-tab sync — see store.ts).
				if (!action.meta.arg?.quiet) state.authReady = false
			})
			.addCase(bootstrapAuthAction.fulfilled, (state, action) => {
				// null = a quiet revalidation that got no server answer; keep state.
				if (action.payload === null) return
				state.user = action.payload.user
				state.isAuth = Boolean(action.payload.user)
				state.deviceUnverified = action.payload.deviceUnverified
				state.chat = action.payload.chat ?? null
				// Rebuild the route back to the challenge. Without this a student who
				// reloads while unverified is stranded: the profile query is gated on
				// !deviceUnverified, so the one authenticated request that would have
				// 401'd and redirected them never fires, and nothing else knows to
				// send them to /verify-device. They land on the home page looking
				// signed in with an empty header and no way forward.
				const restored = action.payload.deviceUnverified ? RedirectCodes.DEVICE_VERIFICATION : action.payload.redirect
				state.redirect = restored ?? null
				state.needRedirect = Boolean(restored)
				state.authReady = true
			})
			.addCase(bootstrapAuthAction.rejected, state => {
				// The thunk swallows its own errors, so this only fires on a
				// programming fault. Treat it as anonymous rather than hanging.
				state.user = null
				state.isAuth = false
				state.authReady = true
			})

		builder
			.addCase(redeemImpersonationAction.fulfilled, (state, action) => {
				state.user = action.payload
				state.isAuth = Boolean(action.payload)
				state.authReady = true
			})
			.addCase(redeemImpersonationAction.rejected, state => {
				// Spent, expired or forged code. Leave whatever bootstrap decided.
				state.authReady = true
			})
	},
})

export const { logoutAction, checkAuthAction, deviceVerifiedAction } = authenticationSlice.actions
export default authenticationSlice.reducer

export const AuthApi = BaseApi.injectEndpoints({
	endpoints: builder => ({
		verifyPhone: builder.query<VerifyPhoneResponsePayload, VerifyPhoneRequestPayload>({
			query: (payload: { token: string }) => {
				return { url: `/auth/phone/confirm`, method: 'POST', body: payload }
			},
		}),
		completeProfile: builder.query<ApiResponse<AuthUserData>, CompleteProfileForm>({
			query: (payload: CompleteProfileForm) => {
				return { url: '/student/profile/complete', method: 'PUT', body: payload }
			},
		}),
		forgetPassword: builder.query<ApiResponse<any>, ForgetPasswordPayload>({
			query: payload => {
				return { url: '/auth/password/forget', method: 'POST', body: payload }
			},
		}),
		resetPassword: builder.query<ApiResponse<any>, ResetPasswordPayload>({
			query: payload => ({ url: '/auth/password/reset', method: 'POST', body: payload }),
		}),
		resendPhone: builder.query<ApiResponse<any>, string>({
			query: payload => ({ url: '/auth/phone/verification/resend', method: 'POST', body: { phone_number: payload } }),
		}),
		changePhone: builder.query<ApiResponse<any>, string>({
			// Pre-verification typo fix. The server updates the number only while
			// phone_verified is still false and sends a fresh OTP to it; a number
			// that already has an account comes back as a 409 with a show:true
			// message, which the base-query interceptor surfaces.
			query: payload => ({ url: '/auth/phone/change', method: 'POST', body: { phone_number: payload } }),
		}),
		resendOTP: builder.query<ApiResponse<any>, string>({
			query: payload => ({ url: '/auth/password/verification/resend', method: 'POST', body: { identifier: payload } }),
		}),
		checkPasswordVerification: builder.query<ApiResponse<any>, CheckResetCodePayload>({
			// Takes the identifier through rather than wrapping a bare string: the
			// server scopes the code to one account now.
			query: payload => ({ url: '/auth/password/verification/check', method: 'POST', body: payload }),
		}),
	}),
})

export const {
	useLazyVerifyPhoneQuery,
	useLazyCompleteProfileQuery,
	useLazyForgetPasswordQuery,
	useLazyResetPasswordQuery,
	useLazyResendPhoneQuery,
	useLazyChangePhoneQuery,
	useLazyResendOTPQuery,
	useLazyCheckPasswordVerificationQuery,
} = AuthApi
